import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { TOURS } from '@/tours';
import { useTRPC } from '@/api/query';
import { useSession } from '@/lib/session';
import { prefersReducedMotion, useAppStore } from '@/lib/store';
import { usePageMeta } from './nav';

const ALL = '__all';
/** At most one automatic tour per browser session; Help → "Show me around" can always replay one. */
const SESSION_KEY = 'keel:tour-session';
const tourShownThisSession = (): boolean => {
  try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch { return false; }
};
const markTourShown = () => {
  try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* storage blocked */ }
};

const startTour = (tourId: string, onFinish: (status: 'done' | 'dismissed') => void): Driver | null => {
  const def = TOURS[tourId];
  if (!def) return null;
  const present = (s: DriveStep) => typeof s.element !== 'string' || document.querySelector(s.element);
  // area tours start on the area's index page; sub-pages (no first anchor) don't get a partial tour
  if (!def.steps[0] || !present(def.steps[0])) return null;
  const steps = def.steps.filter(present);
  let dismissedAll = false;
  let finished = false;
  // driver.js only calls onDestroyed once a step has finished animating in, so record the outcome ourselves too
  const finish = (status: 'done' | 'dismissed') => {
    if (finished) return;
    finished = true;
    onFinish(status);
  };
  const d = driver({
    steps: [...steps],
    showProgress: true,
    animate: !prefersReducedMotion(),
    allowClose: true,
    overlayOpacity: 0.45,
    stagePadding: 6,
    stageRadius: 10,
    popoverClass: 'keel-tour',
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    progressText: '{{current}} of {{total}}',
    onPopoverRender: (popover) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = "Don't show tours";
      btn.title = 'Turn off guided tours everywhere (Help can still replay one)';
      btn.className = 'keel-tour-optout';
      btn.onclick = () => {
        dismissedAll = true;
        finish('dismissed');
        d.destroy();
      };
      popover.footerButtons.prepend(btn);
    },
    onDestroyed: () => finish(dismissedAll ? 'dismissed' : 'done'),
  });
  d.drive();
  return d;
};

/**
 * Runs per-route first-run tours (driver.js). A route opts in with `handle.tourId`; the tour runs once (after
 * the page settles) unless the user chose "Don't show tours again". `requestTour()` (Help panel) replays it.
 */
export const TourProvider = () => {
  const handle = usePageMeta();
  const { pathname } = useLocation();
  const tours = useAppStore((s) => s.tours);
  const tourRequest = useAppStore((s) => s.tourRequest);
  const setTour = useAppStore((s) => s.setTour);
  const active = useRef<Driver | null>(null);
  /** Set when a tour is closed for a dialog: don't record it as seen. */
  const quiet = useRef(false);
  const lastRequest = useRef(tourRequest);
  const tourId = handle?.tourId;
  // "Don't show tours" is also kept on the account (onboardingState.toursSkipped) so it holds on other devices and after sign-out
  const { user } = useSession();
  const trpc = useTRPC();
  const prefs = useMutation(trpc.auth.updatePreferences.mutationOptions({ meta: { silent: true } }));
  const skippedOnAccount = user?.onboardingState?.toursSkipped === true;

  useEffect(() => {
    if (!tourId) return;
    const replay = tourRequest !== lastRequest.current;
    lastRequest.current = tourRequest;
    if (!replay && (tours[tourId] || tours[ALL] || skippedOnAccount || tourShownThisSession())) return;
    const t = setTimeout(() => {
      active.current?.destroy();
      // never start on top of an open dialog or drawer
      if (document.querySelector('[role="dialog"]:not(.driver-popover), [role="alertdialog"]')) return;
      quiet.current = false;
      if (!replay && tourShownThisSession()) return;
      active.current = startTour(tourId, (status) => {
        active.current = null;
        if (quiet.current) {
          quiet.current = false;
          return;
        }
        // "Don't show tours" turns every tour off for good (persisted with the UI prefs); finishing one marks just that one
        if (status === 'dismissed') {
          setTour(ALL, 'dismissed');
          if (user && !skippedOnAccount) prefs.mutate({ onboardingState: { ...user.onboardingState, toursSkipped: true } });
        }
        setTour(tourId, status);
      });
      if (active.current) markTourShown();
    }, replay ? 50 : 900);
    return () => clearTimeout(t);
    // tours intentionally omitted: finishing a tour must not re-trigger this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId, pathname, tourRequest, setTour]);

  useEffect(() => () => active.current?.destroy(), []);

  // a dialog or drawer opening always wins: end the tour (unmarked, so it can run another time) instead of covering it
  useEffect(() => {
    const isUserDialog = (n: Node): boolean =>
      n instanceof HTMLElement && (n.matches('[role="dialog"], [role="alertdialog"]') || Boolean(n.querySelector('[role="dialog"], [role="alertdialog"]'))) && !n.closest('.driver-popover');
    const mo = new MutationObserver((records) => {
      if (!active.current) return;
      if (records.some((r) => [...r.addedNodes].some(isUserDialog))) {
        quiet.current = true;
        active.current.destroy();
      }
    });
    mo.observe(document.body, { childList: true });
    return () => mo.disconnect();
  }, []);
  return null;
};
